/* The local check: Brightivity Helper 0.1.8+ answers http://127.0.0.1:47911/whoami on the computer it runs on,
 * so the page knows which Helper is "this computer" and prints through it without a computer picker.
 * null = no answer (not installed, not running, or the browser refused the local request).
 * Chrome 154+ gates the first request to 127.0.0.1 behind a "local network access" permission prompt: while it is
 * open the fetch waits (so the probe is not aborted then), and a denial stops the probing. */
import { useEffect, useState } from 'react';

export const LOCAL_HELPER_URL = (import.meta.env.VITE_LOCAL_HELPER_URL || 'http://127.0.0.1:47911').replace(/\/+$/, '');

/** The Helper that opened this browser through /connect (Helper 0.1.13+): remembered per browser so the page knows "this
 * computer" even when the browser refuses the local request. {id, claim, at} or null. */
const LOCAL_DEVICE_KEY = 'bv.local.device';
export function rememberLocalDevice(id, claim) {
  try { localStorage.setItem(LOCAL_DEVICE_KEY, JSON.stringify({ id, claim, at: Date.now() })); } catch { /* storage off */ }
}
export function rememberedLocalDevice() {
  try {
    const d = JSON.parse(localStorage.getItem(LOCAL_DEVICE_KEY) || 'null');
    return d && d.id && Date.now() - (d.at || 0) < 90 * 86400e3 ? { helper: true, deviceId: d.id, claim: d.claim || null, remembered: true } : null;
  } catch { return null; }
}

/** 'granted' | 'prompt' | 'denied' | null (browser has no such permission: the request just goes through or fails). */
export async function localPermission() {
  try {
    if (!navigator.permissions || !navigator.permissions.query) return null;
    const p = await navigator.permissions.query({ name: 'local-network-access' });
    return p.state || null;
  } catch {
    return null;
  }
}

export async function probeLocalHelper(timeoutMs = 1500) {
  const perm = await localPermission();
  if (perm === 'denied') return { local: null, permission: perm };
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), perm === 'prompt' ? 60000 : timeoutMs);
  try {
    const r = await fetch(`${LOCAL_HELPER_URL}/whoami`, { signal: ctl.signal, cache: 'no-store' });
    if (!r.ok) return { local: null, permission: perm };
    const j = await r.json();
    return { local: j && j.helper ? j : null, permission: perm === 'prompt' ? 'granted' : perm };
  } catch {
    return { local: null, permission: await localPermission() };
  } finally {
    clearTimeout(t);
  }
}

/** {local, permission, probed}: local = the Helper on this computer (or null), probed = the first check has answered. */
export function useLocalHelper(intervalMs = 15000) {
  const [state, setState] = useState({ local: null, permission: null, probed: false });
  useEffect(() => {
    let alive = true;
    let busy = false;
    let last = null;   // the last Helper this computer answered with
    let misses = 0;
    const tick = async () => {
      if (busy) return;   // a probe waiting on the permission prompt
      busy = true;
      const perm = await localPermission();
      // say "Chrome is asking…" — but never drop a Helper we already know (that flipped the page to another
      // computer's printers every 15 s while the permission sat at "prompt", 2026-10-21)
      if (perm === 'prompt' && !last && alive) setState((s) => ({ ...s, permission: 'prompt', probed: true }));
      const r = await probeLocalHelper();
      busy = false;
      if (r.local) {
        last = r.local; misses = 0;
        if (r.local.deviceId && r.local.claim) rememberLocalDevice(r.local.deviceId, r.local.claim);
      } else {
        misses += 1;
        // A busy page (adding a poster, nesting) can time a probe out: keep the known Helper for a few misses,
        // then the one this browser remembers. Only a run of failures means the Helper is really gone.
        if (last && misses < 4) r.local = last;
        else { const remembered = rememberedLocalDevice(); if (remembered) r.local = remembered; }
      }
      if (alive) setState({ ...r, probed: true });
    };
    tick();
    const t = setInterval(tick, intervalMs);
    return () => { alive = false; clearInterval(t); };
  }, [intervalMs]);
  return state;
}
